import {action, observable} from "mobx";
import {ICartItemParameter} from "../models/cart/cart-item-parameter";
import {NoticeTypeEnum} from "../models/notice";
import {IParameterDto} from "../models/parameter";
import {IProductDetailsDto, IProductListItemDto, IProductSearchDto} from '../models/product/product';
import BaseStore from "./base/base-store";
import RootStore from "./root-store";

export default class ProductStore extends BaseStore {
    public imgRootPath: string;

    @observable
    public isFetchingProductData = false;

    @observable
    public isFetchingParameters: boolean = false;

    @observable
    public isFetchingPrice: boolean = false;

    @observable
    public products: IProductListItemDto[];

    @observable
    public productDetails: IProductDetailsDto;

    @observable
    public parameters: IParameterDto[];

    @observable
    public selectedParameters: ICartItemParameter[] = [];

    @observable
    public priceTotal: number;

    @observable
    public selectedQty: number = 1;

    constructor(rootStore: RootStore) {
        super(rootStore);
        this.imgRootPath = `${process.env.REACT_APP_WEB_SERVICE_ENDPOINT}/img/product`;
    }

    public async searchProducts(request: IProductSearchDto = {}) {
        try {
            this.startFetchingProductData();
            const result = await this.rootStore.productApi.searchProducts(request);
            this.setProducts(result.data);
        } catch (e) {
            this.notify(NoticeTypeEnum.Failure, e);
        } finally {
            this.finishFetchingProductData();
        }
    }

    public async getProductDetails(id: number) {
        try {
            this.startFetchingProductData();
            const result = await this.rootStore.productApi.getProductDetails(id);
            this.setProductDetails(result.data);
        } catch (e) {
            this.notify(NoticeTypeEnum.Failure, e);
        } finally {
            this.finishFetchingProductData();
        }
    }

    public async getParametersByProduct() {
        const rootStore = this.rootStore;
        const product = rootStore.productStore.productDetails;
        const productId = product && product.id;

        if (productId) {
            try {
                this.startFetchingParameters();
                const result = await rootStore.parameterApi.getParametersByProduct(productId);
                this.setParameters(result.data);
            } catch (e) {
                this.notify(NoticeTypeEnum.Failure, e);
            } finally {
                this.finishFetchingParameters();
            }
        }
    }

    public async selectParameterValue(parameter: ICartItemParameter) {
        this.updateSelectedParameters(parameter);
        await this.updateProductPrice();
    }

    @action
    public resetProductDetails() {
        this.selectedParameters = [];
        this.parameters = null;
        this.priceTotal = null;
        this.selectedQty = 1;
        this.productDetails = null;
    }

    @action
    public updatePriceTotal(qty: number) {
        this.selectedQty = qty;

        if (this.productDetails.price) {
            this.priceTotal = this.productDetails.price * this.selectedQty;
        } else {
            this.priceTotal = null;
        }
    }

    private updateSelectedParameters(parameter: ICartItemParameter) {
        const existingParameter = this.selectedParameters.find(x => x.name === parameter.name);

        if (existingParameter) {
            if (parameter.value) {
                existingParameter.value = parameter.value;
            } else {
                const existingIdx = this.selectedParameters.indexOf(existingParameter);
                this.selectedParameters.splice(existingIdx, 1);
            }
        } else {
            if (parameter.value) {
                this.selectedParameters.push(parameter);
            }
        }
    }

    private async updateProductPrice() {
        const parameterValueIds = this.selectedParameters.map(selectedParameter => selectedParameter.value.id);

        try {
            this.startFetchingPrice();
            const result = await this.rootStore.parameterApi.getProductPrice(this.productDetails.id, parameterValueIds);
            this.setPrice(result.data);
        } catch (e) {
            this.notify(NoticeTypeEnum.Failure, e);
        } finally {
            this.finishFetchingPrice();
        }
    }

    @action
    private startFetchingProductData() {
        this.isFetchingProductData = true;
    }

    @action
    private finishFetchingProductData() {
        this.isFetchingProductData = false;
    }

    @action
    private startFetchingParameters() {
        this.isFetchingParameters = true;
    }

    @action
    private finishFetchingParameters() {
        this.isFetchingParameters = false;
    }

    @action
    private startFetchingPrice() {
        this.isFetchingPrice = true;
    }

    @action
    private finishFetchingPrice() {
        this.isFetchingPrice = false;
    }

    @action
    private setProducts(products: IProductListItemDto[]) {
        this.products = products;
    }

    @action
    private setProductDetails(product: IProductDetailsDto) {
        this.productDetails = product;
        this.updatePriceTotal(1);
    }

    @action
    private setParameters(parameters: IParameterDto[]) {
        this.parameters = parameters;
    }

    private setPrice(price: number) {
        this.productDetails.price = price;
        this.updatePriceTotal(this.selectedQty);
    }
}



// WEBPACK FOOTER //
// ./src/stores/product-store.ts